import {
  Component,
  Input,
  OnInit,
  Optional,
  Host,
  SkipSelf,
  OnDestroy,
  ElementRef,
  ViewChild,
  forwardRef,
  Output,
  EventEmitter
} from '@angular/core';
import {
  ControlValueAccessor,
  ControlContainer,
  AbstractControl,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  UntypedFormGroup,
  UntypedFormControl,
  Validator,
  Validators
} from '@angular/forms';
import { FormFields } from '../../services/form-fields-generator.service';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-angular';
import { Item } from '@sitecore-jss/sitecore-jss/layout';
import { GetItemListFieldsService } from '../../services/get-item-list-fields.service';
import {
  BankByBICResponse,
  BankByBICService
} from '../../../transfers/services/bank-by-bic.service';
import { FormAllFieldsValidationService } from '../../services/form-all-fields-validation.service';
import { Subject } from 'rxjs';
import { ConfigurationSettingsContextService } from '../../services/configuration-settings-context.service';
import SwiftFeePayee from '../../../transfers/enums/swift-fee-payee';

@Component({
  selector: 'ab-foreign-bank',
  templateUrl: './foreign-bank.component.html',
  styleUrls: ['./foreign-bank.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ForeignBankComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ForeignBankComponent),
      multi: true
    }
  ]
})
export class ForeignBankComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() formFields: FormFields;
  @Input() rendering: ComponentRendering;
  @Input() formControlName: string;
  @Input() hideCharges: boolean;
  @Output() beneficiaryBankName: EventEmitter<any> = new EventEmitter();
  @Output() intermediaryBankName: EventEmitter<any> = new EventEmitter();
  @ViewChild('beneficiaryBankValueInput') beneficiaryBankValueInput: ElementRef;
  @ViewChild('intermediaryBankValueInput')
  intermediaryBankValueInput: ElementRef;

  public bicValidators = [
    Validators.pattern('^(([a-zA-Z0-9 ]{8})*|([a-zA-Z0-9 ]{11}))*$'),
    Validators.minLength(this.validationPaymentsBICCodeMinLength),
    Validators.maxLength(this.validationPaymentsBICCodeLength)
  ];

  private abaValidators = [
    Validators.pattern('[0-9]+'),
    Validators.maxLength(this.validationAddressBookABALength),
    Validators.minLength(this.validationAddressBookABALength)
  ];

  public fields: { [key: string]: { [name: string]: Field } };
  public foreignBankForm = new UntypedFormGroup({
    swiftFeePayeeMethod: new UntypedFormControl('1', [Validators.required]),
    beneficiaryBankType: new UntypedFormControl(null),
    beneficiaryBankValue: new UntypedFormControl(null, [
      Validators.required,
      ...this.bicValidators
    ]),
    intermediaryBankType: new UntypedFormControl(null),
    intermediaryBankValue: new UntypedFormControl(null, this.bicValidators)
  });
  public onChange: Function;
  public onTouched: Function;
  public beneficiaryBank: BankByBICResponse = <BankByBICResponse>{};
  public intermediaryBank: BankByBICResponse = <BankByBICResponse>{};
  public beneficiaryBankMaxLength: number;
  public beneficiaryBankMinLength: number;
  public intermediaryBankMaxLength: number;
  public intermediaryBankMinLength: number;

  private formControl: AbstractControl;

  constructor(
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer,
    private getItemListFieldsService: GetItemListFieldsService,
    protected bankByBICService: BankByBICService,
    private formAllFieldsValidationService: FormAllFieldsValidationService,
    private configurationSettingsContextService: ConfigurationSettingsContextService
  ) {
    this.beneficiaryBankMaxLength = this.validationPaymentsBICCodeLength;
    this.intermediaryBankMaxLength = this.validationPaymentsBICCodeLength;
  }

  get beneficiaryBankValue() {
    return this.foreignBankForm.get('beneficiaryBankValue');
  }

  get intermediaryBankValue() {
    return this.foreignBankForm.get('intermediaryBankValue');
  }

  get swiftFeePayeeMethod() {
    return this.foreignBankForm.get('swiftFeePayeeMethod');
  }

  get beneficiaryBankType() {
    return this.foreignBankForm.get('beneficiaryBankType');
  }

  get intermediaryBankType() {
    return this.foreignBankForm.get('intermediaryBankType');
  }

  private get validationPaymentsBICCodeLength() {
    return parseInt(
      this.configurationSettingsContextService?.getConfigurationSettingsValidationAttributeValue(
        'ValidationPaymentsBICCodeLength'
      ),
      0
    );
  }

  private get validationPaymentsBICCodeMinLength() {
    return parseInt(
      this.configurationSettingsContextService?.getConfigurationSettingsValidationAttributeValue(
        'ValidationPaymentsBICCodeMinLength'
      ),
      0
    );
  }

  private get validationAddressBookABALength() {
    return parseInt(
      this.configurationSettingsContextService?.getConfigurationSettingsValidationAttributeValue(
        'ValidationAddressBookABALength'
      ),
      0
    );
  }

  ngOnInit() {
    this.formControl = this.controlContainer.control.get(this.formControlName);
    this.formControl.enable();
    this.formAllFieldsValidationService.addSubForms(this.foreignBankForm);

    if (!this.hideCharges) {
      this.fields = this.getItemListFieldsService.getFields(
        (this.rendering.fields.items as Item[]).filter(
          o => o.name === 'ForeignBankCharges'
        )[0]
      );
    }

    this.beneficiaryBankType.setValue(
      (this.formFields.fields.internationalBeneficiaryBankDD.values[0] as any)
        .fields.label.value
    );

    this.intermediaryBankType.setValue(
      (this.formFields.fields.intermediaryBeneficiaryBankDD.values[0] as any)
        .fields.label.value
    );

    this.beneficiaryBankType.valueChanges.subscribe(value => {
      this.beneficiaryBankValue.setValue(null);
      this.beneficiaryBank = <BankByBICResponse>{};
      setTimeout(() => {
        this.beneficiaryBankValueInput.nativeElement.focus();
      });
      this.setBeneficiaryBankValidators();
    });

    this.intermediaryBankType.valueChanges.subscribe(value => {
      this.intermediaryBankValue.setValue(null);
      this.intermediaryBank = <BankByBICResponse>{};
      setTimeout(() => {
        this.intermediaryBankValueInput.nativeElement.focus();
      });
      this.setIntermediaryBankValidators();
    });
  }

  setBeneficiaryBankValidators() {
    if (this.isABA(this.beneficiaryBankType)) {
      this.beneficiaryBankValue.clearValidators();
      this.beneficiaryBankValue.setValidators([
        Validators.required,
        ...this.abaValidators
      ]);
      this.beneficiaryBankMaxLength = this.validationAddressBookABALength;
      this.beneficiaryBankMinLength = this.validationAddressBookABALength;
    } else {
      this.beneficiaryBankValue.clearValidators();
      this.beneficiaryBankValue.setValidators([
        Validators.required,
        ...this.bicValidators
      ]);
      this.beneficiaryBankMaxLength = this.validationPaymentsBICCodeLength;
      this.beneficiaryBankMinLength = 1;
    }
  }

  setIntermediaryBankValidators() {
    if (this.isABA(this.intermediaryBankType)) {
      this.intermediaryBankValue.clearValidators();
      this.intermediaryBankValue.setValidators(this.abaValidators);
      this.intermediaryBankMaxLength = this.validationAddressBookABALength;
      this.intermediaryBankMinLength = this.validationAddressBookABALength;
    } else {
      this.intermediaryBankValue.clearValidators();
      this.intermediaryBankValue.setValidators(this.bicValidators);
      this.intermediaryBankMaxLength = this.validationPaymentsBICCodeLength;
      this.intermediaryBankMinLength = 0;
    }
  }

  ngOnDestroy() {
    this.formControl.clearValidators();
    this.formControl.disable();
    this.formControl.markAsPristine();
    this.formControl.markAsUntouched();
    this.formControl.patchValue(null);
  }

  writeValue(value) {
    if (!value) {
      this.beneficiaryBank = <BankByBICResponse>{};
      this.intermediaryBank = <BankByBICResponse>{};

      this.foreignBankForm.reset(
        {
          swiftFeePayeeMethod: '1',
          beneficiaryBankType: (this.formFields.fields
            .internationalBeneficiaryBankDD.values[0] as any).fields.label
            .value,
          beneficiaryBankValue: null,
          intermediaryBankType: (this.formFields.fields
            .internationalBeneficiaryBankDD.values[0] as any).fields.label
            .value,
          intermediaryBankValue: null
        },
        {
          onlySelf: true,
          emitEvent: false
        }
      );

      setTimeout(() => {
        this.setBeneficiaryBankValidators();
        this.setIntermediaryBankValidators();
      });
    } else {
      this.foreignBankForm.patchValue(
        {
          swiftFeePayeeMethod: SwiftFeePayee[
            value.swiftFeePayeeMethod
          ].toString(),
          beneficiaryBankType: value.beneficiaryBankType,
          intermediaryBankType: value.intermediaryBankType || 'BIC'
        },
        { emitEvent: false }
      );

      this.beneficiaryBank = undefined;
      this.intermediaryBank = undefined;

      this.setBeneficiaryBankValidators();
      this.setIntermediaryBankValidators();

      this.foreignBankForm.patchValue({
        beneficiaryBankValue: value.beneficiaryBankValue,
        intermediaryBankValue: value.intermediaryBankValue
      });

      setTimeout(() => {
        this.beneficiaryBankValidationBic();
        this.intermediaryBankValueValidationBic();
      });
    }
  }

  registerOnChange(fn: any) {
    this.foreignBankForm.valueChanges.subscribe(fn);
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  touch() {
    this.onTouched();
  }

  validate() {
    if (this.foreignBankForm.invalid) {
      return { formInvalid: true };
    }

    return null;
  }

  beneficiaryBankValidationBic() {
    if (this.beneficiaryBankValue.invalid) {
      this.beneficiaryBank = undefined;
      return;
    }

    if (this.isABA(this.beneficiaryBankType)) {
      return;
    }
    this.beneficiaryBankValue.setValue(
      this.beneficiaryBankValue.value.toUpperCase()
    );
    this.checkBicCode(this.beneficiaryBankValue).subscribe(result => {
      this.beneficiaryBank = result;
      this.beneficiaryBankName.emit(this.beneficiaryBank.name);
    });

    this.onTouched();
  }

  intermediaryBankValueValidationBic() {
    if (
      this.intermediaryBankValue.invalid ||
      !this.intermediaryBankValue.value
    ) {
      this.intermediaryBank = undefined;
      return;
    }

    if (this.isABA(this.intermediaryBankType)) {
      return;
    }
    this.intermediaryBankValue.setValue(
      this.intermediaryBankValue.value.toUpperCase()
    );

    this.checkBicCode(this.intermediaryBankValue).subscribe(result => {
      this.intermediaryBank = result;
      this.intermediaryBankName.emit(this.intermediaryBank.name);
    });

    this.onTouched();
  }

  protected isABA(control: AbstractControl): boolean {
    return (
      control.value ===
      (this.formFields.fields.intermediaryBeneficiaryBankDD.values[1] as any)
        .fields.label.value
    );
  }

  protected checkBicCode(control: AbstractControl) {
    const subject = new Subject<BankByBICResponse>();

    this.bankByBICService.getBankByBIC(control.value).subscribe(bankInfo => {
      if (bankInfo.bicCode) {
        subject.next(bankInfo);
      } else {
        // Trigger Transfer form validation
        this.onChange(this.foreignBankForm.value);
        subject.next(<BankByBICResponse>{});
      }
    });

    return subject.asObservable();
  }
}
